import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <main className="pt-24 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  return (
    <>
      {/* JSON-LD Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.publishDate,
            author: {
              '@type': 'Organization',
              name: '网络安全教育团队',
            },
            publisher: {
              '@type': 'Organization',
              name: '网络安全教育团队',
            },
            about: {
              '@type': 'Thing',
              name: article.tags[0],
            },
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: '黄网识别与防范指南',
              url: 'https://fangfanhuangwang.com',
            },
          }),
        }}
      />

      <main className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                {
                  label: categoryInfo?.name || article.category,
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap">
                {article.tags[0]}
              </span>
              {article.tags.slice(1).map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-400 bg-background-100 px-3 py-1 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-6">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>
                阅读量 1.2k
              </span>
            </div>
          </header>

          {/* Article Content */}
          <article className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-10 mb-12">
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {/* Article Footer Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-12">
            <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">文章标签：</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category/${article.category}`}
                className="text-xs text-foreground-500 bg-background-100 hover:bg-primary-50 hover:text-primary-600 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
              >
                {tag}
              </Link>
            ))}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((relArticle) => (
                  <ArticleCard key={relArticle.slug} article={relArticle} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}